import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  clearWallet,
  loadWallet,
  saveWallet,
  type LoadResult,
  type UnlockedWallet,
} from "./storage/wallet-state";
import { isValidPhrase, normalizePhrase } from "./crypto/mnemonic";

/**
 * Wallet states observable by the UI:
 *
 *  - "boot"          — initial — we haven't tried to load yet
 *  - "none"          — no wallet stored; show setup
 *  - "unlocked"      — wallet loaded, addresses available, can sign
 *  - "needs-restore" — ciphertext exists but key is missing (cleared cache /
 *                       new device) — user must paste mnemonic to restore
 *  - "error"         — something went wrong loading
 */
export type WalletState =
  | { kind: "boot" }
  | { kind: "none" }
  | { kind: "unlocked"; wallet: UnlockedWallet }
  | { kind: "needs-restore"; reason: string }
  | { kind: "error"; message: string };

export type WalletApi = {
  state: WalletState;
  /** Persist a freshly-generated or imported mnemonic. */
  setup: (phrase: string) => Promise<UnlockedWallet>;
  /** Reload from storage — useful after wipe/restore. */
  refresh: () => Promise<void>;
  /** Wipe storage and return to "none". */
  reset: () => Promise<void>;
};

const Ctx = createContext<WalletApi | null>(null);

const IDLE_MS = 15 * 60 * 1000;

const fromLoad = (r: LoadResult): WalletState => {
  switch (r.kind) {
    case "unlocked":
      return { kind: "unlocked", wallet: { phrase: r.phrase, addresses: r.addresses } };
    case "none":
      return { kind: "none" };
    case "needs-restore":
      return { kind: "needs-restore", reason: r.reason };
    case "error":
      return { kind: "error", message: r.message };
  }
};

export function WalletProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WalletState>({ kind: "boot" });
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    const result = await loadWallet();
    setState(fromLoad(result));
  }, []);

  // Boot — try to load on mount
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Idle re-lock: 15 minutes after visibility loss, clear the in-memory phrase
  // by reloading from storage. The next interaction silently re-decrypts.
  useEffect(() => {
    const armTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        if (state.kind === "unlocked") void refresh();
      }, IDLE_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") armTimer();
      else if (idleTimer.current) clearTimeout(idleTimer.current);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [state.kind, refresh]);

  const setup = useCallback(async (phrase: string) => {
    const normalized = normalizePhrase(phrase);
    if (!isValidPhrase(normalized)) {
      throw new Error("Invalid mnemonic phrase.");
    }
    const wallet = await saveWallet(normalized);
    setState({ kind: "unlocked", wallet });
    return wallet;
  }, []);

  const reset = useCallback(async () => {
    await clearWallet();
    setState({ kind: "none" });
  }, []);

  const api = useMemo<WalletApi>(
    () => ({ state, setup, refresh, reset }),
    [state, setup, refresh, reset],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export const useWallet = (): WalletApi => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useWallet must be used inside <WalletProvider>");
  return v;
};

/** Returns the unlocked wallet, or null if not yet ready. Convenient for consumers. */
export const useUnlockedWallet = (): UnlockedWallet | null => {
  const { state } = useWallet();
  return state.kind === "unlocked" ? state.wallet : null;
};
